import { FileDownload as FileDownloadIcon, Info } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import TableCard from 'lib/designSystem/Card/TableCard';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatMoney } from 'lib/functions/utils';
import {
  AmountSummaryHeader,
  AmountSummarySection,
} from 'pages/Finance/Components/Financing/common/Summary';
import React from 'react';

import { PayOutDocument, PayOutRequest } from '../../../../../types';

interface AmountSummaryProps {
  request: PayOutRequest;
  onExport?: () => void | Promise<void>;
}

const AmountSummary: React.FC<AmountSummaryProps> = ({ request, onExport }) => {
  const paymentAmount = request.payments.reduce((acc, payment) => {
    return acc + payment.amount;
  }, 0);

  const hasCompletedPayments = request.payments.some(
    (payment) => payment.executedAt,
  );

  const shouldShowExportButton = (source: string): boolean => {
    return (
      source !== 'payment-request' &&
      source !== 'credit-request' &&
      source !== 'payment-request-passive'
    );
  };

  const exportButton =
    hasCompletedPayments &&
    onExport &&
    shouldShowExportButton(request.source) ? (
      <IconButton
        size="small"
        onClick={() => {
          if (onExport) {
            void onExport();
          }
        }}
        sx={{
          color: 'primary.main',
          backgroundColor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200',
          '&:hover': {
            backgroundColor: 'grey.100',
            borderColor: 'grey.300',
          },
          borderRadius: '100px',
          padding: '4px 12px',
        }}
      >
        <FileDownloadIcon sx={{ fontSize: '1rem', paddingRight: '4px' }} />
        <Typography variant="caption" fontWeight={600}>
          Exportar
        </Typography>
      </IconButton>
    ) : undefined;

  const renderAmountSummary = () => {
    switch (request.source) {
      case 'payment-request-passive': {
        const amountPendingAccounts = request.pendingAccounts.reduce(
          (acc, account) => {
            return acc + account.amount;
          },
          0,
        );
        const amountAppliedDocuments = request.documentsApplied.reduce(
          (acc, document) => acc + document.appliedAmount,
          0,
        );
        const totalToPay = amountPendingAccounts - amountAppliedDocuments;

        return (
          <>
            <AmountSummaryHeader
              title="Resumen Montos"
              totalAmount={totalToPay}
              formatMoney={formatMoney}
              actionButton={exportButton}
            />
            <AmountSummarySection
              items={[
                {
                  label: 'Cuentas pendientes',
                  value: amountPendingAccounts,
                },
                {
                  label: 'Documentos aplicados',
                  value: -amountAppliedDocuments,
                },
                {
                  label: 'Total a pagar',
                  value: totalToPay,
                  valueColor: 'primary.main',
                },
              ]}
              formatMoney={formatMoney}
            />
          </>
        );
      }
      case 'factoring-request': {
        const documentsAmount = request.documents.reduce(
          (acc, document) => acc + document.amount,
          0,
        );
        const discounts = documentsAmount - request.amountToDepositTotal;

        return (
          <>
            <AmountSummaryHeader
              title="Resumen Montos"
              totalAmount={request.amountToDepositTotal}
              formatMoney={formatMoney}
              actionButton={exportButton}
            />
            <AmountSummarySection
              items={[
                {
                  label: 'Documentos',
                  value: documentsAmount,
                },
                {
                  label: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        fontWeight={600}
                        variant="caption"
                        color="text.secondary"
                      >
                        Descuentos
                      </Typography>
                      <LightTooltip
                        title={
                          <Box
                            sx={{
                              p: 0.75,
                              maxWidth: 360,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                            }}
                          >
                            <Typography variant="body1" fontWeight="medium">
                              Para más detalles, ir a la sección de{' '}
                              <a href="/finance/financing/factoring">
                                factoring
                              </a>
                            </Typography>
                          </Box>
                        }
                      >
                        <Info
                          sx={{
                            marginLeft: 0.75,
                            fontSize: 16,
                            cursor: 'pointer',
                            color: 'text.secondary',
                          }}
                        />
                      </LightTooltip>
                    </Box>
                  ),
                  value: discounts,
                },
                {
                  label: 'Total a pagar',
                  value: request.amountToDepositTotal,
                  valueColor: 'primary.main',
                },
              ]}
              formatMoney={formatMoney}
            />
          </>
        );
      }
      case 'payment-request': {
        const getDocumentAmount = (
          doc: PayOutDocument & { type?: string; netAmount?: number },
        ): number => {
          if (doc.type === 'receipt-fee') {
            return doc.netAmount ?? doc.amount;
          }
          return doc.amount;
        };
        const documentsAmount = request.documents.reduce(
          (acc, document) => acc + getDocumentAmount(document),
          0,
        );

        return (
          <>
            <AmountSummaryHeader
              title="Resumen Montos"
              totalAmount={documentsAmount}
              formatMoney={formatMoney}
            />
            <AmountSummarySection
              items={[
                {
                  label: 'Documentos',
                  value: documentsAmount,
                },
                {
                  label: 'Total a pagar',
                  value: documentsAmount,
                  valueColor: 'primary.main',
                },
              ]}
              formatMoney={formatMoney}
            />
          </>
        );
      }
      case 'credit-request': {
        return (
          <>
            <AmountSummaryHeader
              title="Resumen Montos"
              totalAmount={paymentAmount}
              formatMoney={formatMoney}
              actionButton={exportButton}
            />
            <AmountSummarySection
              items={[
                {
                  label: 'Monto crédito',
                  value: request.amount,
                },
                {
                  label: 'Monto en garantía',
                  value: request.guaranteeAmount,
                },
                {
                  label: 'Total a pagar',
                  value: paymentAmount,
                  valueColor: 'primary.main',
                },
              ]}
              formatMoney={formatMoney}
            />
          </>
        );
      }
      default:
        return null;
    }
  };

  return (
    <TableCard
      size="medium"
      enabledEffects={false}
      sx={{ boxShadow: 'none', background: 'transparent' }}
    >
      {renderAmountSummary()}
    </TableCard>
  );
};

export default AmountSummary;
